/**
 * Writing a calibre's data onto references — the pure payload builders and the
 * batched writers behind the Bulk Calibre Enricher's two Apply buttons, and
 * behind the Variants panel's per-parent "Push parent calibre".
 *
 * Extracted from BulkCalibreEnricher.tsx so a second caller can use them without
 * importing a 2,000-line React component. Behaviour is unchanged: the enricher
 * imports every one of these back, and re-exports the ones its tests import.
 */
import { doc, writeBatch, deleteField, Timestamp } from 'firebase/firestore';
import {
  db,
  deriveFunctionsFromCalibre,
  type CustomCalibreEntry,
  type CalibreData,
} from '../../services/firebase';
import {
  buildCalibreIdIndex,
  findRefCalibreEntry,
  isCatchAllCalibre,
  isDeprecatedCalibre,
  resolveDisplayCalibre,
} from '../../services/calibreResolution';
import { buildCalibreLinkFields, type CalibreDualWriteFlags } from './calibreBulkUpdate';
import { isTierStaleOnCalibre } from './aiCalibreApply';
import { deriveIsVariant } from '../../utils/references';
import type { MovementInfo, WatchReference } from '../../types';

const WATCH_BRANDS_COLLECTION = 'watchBrands';
const REFERENCES_SUBCOLLECTION = 'references';

/**
 * One variant in the Missing section whose PARENT already names a calibre —
 * the unit of work behind "Apply Parent Calibre to Variants".
 *
 * Exists because enrichment only ever reaches parents. The refs listing is
 * parents-only, Algolia indexes parents only, and every enrichment tool (this
 * screen's Apply included) therefore works on parents — so a Shopify product's
 * children keep the empty calibre they were imported with while their parent
 * gets curated. Nothing propagates a calibre DOWNWARD: watchlock's
 * `propagateVariantAggregatesToParent` rolls variant values UP into the
 * parent's `movementVariants`, and there is no mirror going the other way.
 * Copying by hand, one variant at a time, was the only fix available.
 *
 * The parent is found by `parentReferenceId` — the ONLY parent join that
 * exists. It is deliberately not derived from the reference string: Heinrich's
 * variants are `radiance-1 … radiance-24`, which look stem-derivable, but
 * `radiance-guilloche-v2-2` would stem to another product's parent ref and
 * `radiance-2` and `radiance-guilloche-2` belong to different products
 * entirely. The doc-id link is exact; the string pattern is a brand's SKU
 * convention that happens to look like one.
 */
export interface ParentCalibreFix {
  /** The variant being filled in. */
  ref: WatchReference;
  /** Its parent — the doc `ref.parentReferenceId` names. */
  parent: WatchReference;
  /** The calibre the parent resolves to. */
  entry: CustomCalibreEntry;
  /**
   * The parent's `calibreTierId`, but ONLY when that tier is one `entry`
   * actually defines. Undefined is the overwhelmingly common case and a
   * perfectly valid one — a tier is meaningful only on a calibre with
   * `tiers[]`, and generic base calibres (the ETA/Sellita movements most
   * micro-brands fit) have none.
   */
  tierId?: string;
}

// ---------------------------------------------------------------------------
// Pushing calibre data back to references
// ---------------------------------------------------------------------------

/** Build Firestore update map for one reference, filling empty fields only.
 *  Uses dot-notation paths so only the targeted nested fields are written.
 *
 *  The whole matched ENTRY comes in, not its spec and name as two arguments: the
 *  `calibreId` link written below and the legacy specs written beside it then
 *  provably describe the same calibre, which is the guarantee resolveCalibreForRef
 *  exists to provide on the other write paths.
 *
 *  The two dual-write flags subtract from the payload; neither adds anything.
 *  With both on — today's default — this returns exactly what it always did,
 *  plus the link. See CalibreDualWriteFlags. */
export function buildRefUpdates(
  ref: WatchReference,
  entry: Pick<CustomCalibreEntry, 'id' | 'name' | 'data'>,
  flags: CalibreDualWriteFlags,
): Record<string, unknown> {
  const updates: Record<string, unknown> = {};
  const d: CalibreData = entry.data || {};
  const calibreName = entry.name;
  const m = (ref.movement || {}) as Record<string, unknown>;

  const hasScalar = (v: unknown): boolean =>
    v !== undefined && v !== null && v !== '';

  // Every setter below EXCEPT setScalarMirror writes a legacy movement field —
  // `movementType`, `movement.*` or `functions` — so each one answers to
  // flags.legacyMovement at source. Gating them here rather than wrapping the
  // eighty-odd calls in a conditional keeps each write reading as the single
  // line it is. setScalarMirror is the odd one out: its only caller is the
  // top-level `calibre` string, gated by the other flag at the call site.
  const setScalar = (path: string, existing: unknown, val: unknown) => {
    if (!flags.legacyMovement) return;
    if (val === undefined || val === null || val === '') return;
    if (hasScalar(existing)) return;
    updates[path] = val;
  };

  const setScalarMirror = (path: string, existing: unknown, val: unknown) => {
    // Mirror the calibre's scalar exactly: write whenever the calibre specifies
    // a non-empty value. Only leave the ref alone when the calibre doesn't
    // specify the field at all.
    if (val === undefined || val === null || val === '') return;
    if (existing === val) return; // no-op write
    updates[path] = val;
  };

  const setMovementMirror = (path: string, existing: unknown, val: unknown) => {
    if (!flags.legacyMovement) return;
    setScalarMirror(path, existing, val);
  };

  const setBoolMirror = (path: string, existing: unknown, val: boolean | undefined) => {
    // Mirror the calibre's boolean: true is always written, and false is
    // written over a value the ref actually carries. Leave the ref alone when
    // the calibre doesn't specify the field at all.
    if (!flags.legacyMovement) return;
    if (val === undefined || val === null) return;
    if (existing === val) return; // no-op write
    // false over NOTHING is not a change — an absent flag already reads as
    // false everywhere. Writing it anyway is what made an unedited Save of a
    // calibre re-flag every one of its references: the editor stores its nine
    // certification/complication flags as explicit booleans, so a calibre that
    // had never been saved gained seven `false`s, and each was then "mirrored"
    // onto every reference (Rolex 2235: 7 flags x 731 refs = 5,117 writes).
    if (val === false && (existing === undefined || existing === null)) return;
    updates[path] = val;
  };

  const setArray = (path: string, existing: unknown, val: string[] | undefined) => {
    if (!flags.legacyMovement) return;
    if (!val || val.length === 0) return;
    if (Array.isArray(existing) && existing.length > 0) return;
    updates[path] = val;
  };

  // Top-level calibre name — mirror the matched calibre's canonical name so the
  // ref's display value stays consistent. Refs still matching via an alias keep
  // matching even after their stored name is rewritten (the matcher uses the
  // calibre's knownAs/alias list), so this is safe.
  if (flags.legacyCalibreString) {
    setScalarMirror('calibre', ref.calibre, calibreName?.trim() || undefined);
  }

  // Top-level movementType (flat string on the ref) — mirror the calibre exactly.
  setMovementMirror('movementType', ref.movementType, d.movementType);

  // Nested movement.* scalars
  setScalar('movement.jewels', m.jewels, d.jewels);
  setScalar('movement.frequencyVph', m.frequencyVph, d.frequencyVph);
  setScalar('movement.powerReserveHours', m.powerReserveHours, d.powerReserveHours);
  setScalar('movement.diameterMm', m.diameterMm, d.diameterMm);
  setScalar('movement.heightMm', m.heightMm, d.heightMm);
  setScalar('movement.liftAngleDegrees', m.liftAngleDegrees, d.liftAngleDegrees);
  setScalar('movement.serviceIntervalYears', m.serviceIntervalYears, d.serviceIntervalYears);
  setScalar('movement.casingDiameterMm', m.casingDiameterMm, d.casingDiameterMm);
  setScalar('movement.batteryLifeYears', m.batteryLifeYears, d.batteryLifeYears);
  setScalar('movement.windingDirection', m.windingDirection, d.windingDirection);
  setScalar('movement.countryOfManufacture', m.countryOfManufacture, d.countryOfManufacture);
  setScalar('movement.antiShockSystem', m.antiShockSystem, d.antiShockSystem);
  setScalar('movement.rotorType', m.rotorType, d.rotorType);
  setScalar('movement.lignes', m.lignes, d.lignes);
  setScalar('movement.regulatorSystem', m.regulatorSystem, d.regulatorSystem);
  setScalar('movement.manualWindingDirection', m.manualWindingDirection, d.manualWindingDirection);
  setScalar('movement.hairspring', m.hairspring, d.hairspring);
  setScalar('movement.escapement', m.escapement, d.escapement);
  setScalar('movement.batteryType', m.batteryType, d.batteryType);

  // Nested movement.baseMovement — mirror the calibre's base calibre name exactly.
  setMovementMirror('movement.baseMovement', m.baseMovement, d.baseMovement);

  // Nested movement.* booleans — mirror the calibre (true always; false only
  // over a value the ref carries), leaving the ref alone when the calibre
  // doesn't specify the field.
  setBoolMirror('movement.hacking', m.hacking, d.hacking);
  setBoolMirror('movement.manualWinding', m.manualWinding, d.manualWinding);
  setBoolMirror('movement.quickSetDate', m.quickSetDate, d.quickSetDate);
  setBoolMirror('movement.isCosc', m.isCosc, d.isCosc);
  setBoolMirror('movement.isGlashuetteRegulation', m.isGlashuetteRegulation, d.isGlashuetteRegulation);
  setBoolMirror('movement.isGenevaSeal', m.isGenevaSeal, d.isGenevaSeal);
  setBoolMirror('movement.isGrandSeikoStandard', m.isGrandSeikoStandard, d.isGrandSeikoStandard);
  setBoolMirror('movement.isMETAS', m.isMETAS, d.isMETAS);
  setBoolMirror('movement.isChronometer', m.isChronometer, d.isChronometer);
  setBoolMirror('movement.isChronograph', m.isChronograph, d.isChronograph);
  setBoolMirror('movement.isGMT', m.isGMT, d.isGMT);
  setBoolMirror('movement.isInHouse', m.isInHouse, d.isInHouse);
  setBoolMirror('movement.columnWheel', m.columnWheel, d.columnWheel);
  setBoolMirror('movement.doubleBarrel', m.doubleBarrel, d.doubleBarrel);

  // Nested movement.* arrays
  setArray('movement.date', m.date, d.date);
  setArray('movement.hands', m.hands, d.hands);
  setArray('movement.chronograph', m.chronograph, d.chronograph);
  setArray('movement.additionals', m.additionals, d.additionals);
  setArray('movement.astronomical', m.astronomical, d.astronomical);
  setArray('movement.acoustic', m.acoustic, d.acoustic);

  // Top-level functions: mirror the calibre's derived state exactly. Build
  // purely from the calibre (do NOT seed from the ref's existing array) and
  // replace the ref's array wholesale — including removing entries the calibre
  // no longer implies. An empty derived array is written as [] (clearing the ref).
  if (flags.legacyMovement) {
    const derived = deriveFunctionsFromCalibre(d);
    const existing = ref.functions || [];
    const same =
      derived.length === existing.length &&
      [...derived].sort().every((f, i) => f === [...existing].sort()[i]);
    if (!same) {
      updates.functions = derived;
    }
  }

  // The link, plus the orphan-tier guard. Both flags are irrelevant here: the
  // `calibreId` this writes is the migration's destination, not one of the
  // legacy writes being retired, and a dangling `calibreTierId` is wrong however
  // the legacy fields are configured.
  //
  // The tier is cleared with deleteField() rather than '' — an empty string is
  // a stored value that every reader would have to know to treat as unset.
  Object.assign(
    updates,
    buildCalibreLinkFields(
      { calibreId: entry.id, entry },
      ref,
      { clearTier: deleteField() },
    ),
  );

  return updates;
}

/** Apply the dot-notation updates back to an in-memory WatchReference so the
 *  local cache reflects what was just persisted. */
export function applyUpdatesToRef(
  ref: WatchReference,
  updates: Record<string, unknown>,
): WatchReference {
  const next: WatchReference = { ...ref };
  const movementUpdates: Record<string, unknown> = {};
  let movementTouched = false;
  for (const [path, value] of Object.entries(updates)) {
    if (path === 'updatedAt') continue;
    if (path === 'calibre') {
      next.calibre = value as string;
    } else if (path === 'movementType') {
      next.movementType = value as string;
    } else if (path === 'functions') {
      next.functions = value as string[];
    } else if (path === 'calibreId') {
      next.calibreId = value as string;
    } else if (path === 'calibreTierId') {
      // Only ever the deleteField() sentinel here — this tool clears a dangling
      // tier and never sets one, so the local mirror is always "now unset".
      next.calibreTierId = undefined;
    } else if (path.startsWith('movement.')) {
      movementUpdates[path.slice('movement.'.length)] = value;
      movementTouched = true;
    }
  }
  if (movementTouched) {
    next.movement = {
      ...(next.movement || {}),
      ...movementUpdates,
    } as MovementInfo;
  }
  return next;
}

/** Firestore caps a batch at 500 operations. 400 keeps headroom without
 *  meaningfully changing the round-trip count on a realistic group. */
export const REF_WRITE_BATCH_SIZE = 400;

/**
 * Reference field that tells watchlock's `maintainCalibreReferenceCount` "a
 * bulk run is rewriting this calibre's references and will request ONE recount
 * when it finishes — do not recount on this write". Honoured only when the
 * token changes in the write, so later edits recount as normal. Mirrors
 * `CALIBRE_RECOUNT_DEFER_TOKEN_FIELD` in watchlock `functions/src/bulkWriteGuards.ts`.
 */
export const CALIBRE_RECOUNT_DEFER_TOKEN_FIELD = '__calibreRecountToken';

/**
 * Should this ref's write carry the bulk-run token that makes watchlock's
 * `propagateVariantAggregatesToParent` skip its parent recompute?
 *
 * Two conditions, and both matter:
 *
 *  1. The ref must be a VARIANT. The trigger's own Guard 1 drops any write to a
 *     doc that is not a variant in either state, so a token on a parent or a
 *     standalone ref buys nothing and just leaves a meaningless field behind on
 *     most of the catalogue. Mirrors what the bulk importer does.
 *  2. The write must NOT be changing top-level `calibre`. That field is one of
 *     the seven the trigger aggregates onto the parent, and — unlike the bulk
 *     importer, which computes and writes the parent's arrays itself in a
 *     pre-pass before touching any variant — this tool never touches the parent.
 *     Skipping the recompute on a write that genuinely moves an aggregated value
 *     would drift the parent permanently, correctable only by the backfill
 *     callable. Every other field this tool writes (`movement.*`, `movementType`,
 *     `functions`) is invisible to the aggregates, so the far more common
 *     fill-the-empty-movement-fields write still skips the fan-out, which is
 *     where the contention was.
 */
export function shouldStampBulkToken(
  ref: WatchReference,
  updates: Record<string, unknown>,
): boolean {
  if (!ref.parentReferenceId) return false;
  return updates.calibre === undefined;
}

/**
 * The calibre NAME a reference currently shows, by the same two-step the scan
 * sections on: the linked calibre's name when `calibreId` resolves, else the
 * cached/legacy display string. Empty means the reference is "missing".
 *
 * Shared so the parent-fix list and the Missing section can never disagree
 * about which references have no calibre.
 */
export function displayCalibreForRef(
  ref: WatchReference,
  calibresById: ReadonlyMap<string, CustomCalibreEntry>,
): string {
  const linked = ref.calibreId ? calibresById.get(ref.calibreId.trim()) : undefined;
  return linked?.name?.trim() || resolveDisplayCalibre(ref);
}

/**
 * Every variant with no calibre whose parent has one to give.
 *
 * Pure and exported for tests. Takes the DOC-ID-keyed reference map because
 * `parentReferenceId` is a doc id: the ref-string-keyed map this screen used to
 * scan with cannot resolve a parent at all, and (since a Shopify parent's
 * `reference` is its LEAD VARIANT'S SKU) silently drops one of the two docs
 * that collide on that string.
 *
 * A reference qualifies only when every one of these holds:
 *  1. It has no calibre value — the Missing section's own test.
 *  2. It is a variant, by {@link deriveIsVariant} rather than a truthiness check
 *     on `parentReferenceId` (a whitespace-only value is not a parent link).
 *  3. Its parent is in the map. Within one brand it always is; failing closed
 *     costs nothing and covers a dangling link.
 *  4. The parent is not ITSELF a variant. The importer does not nest, so this
 *     cannot happen — but the guard is one comparison and it means the rule is
 *     "copy from the DIRECT parent" rather than a chain walk that trusts an
 *     invariant legacy data might not honour.
 *  5. The parent's calibre resolves, via the same FK-first/name-second lookup
 *     the rest of the tool uses.
 *  6. That calibre is neither deprecated nor a catch-all. Both already refuse
 *     to match by name everywhere else; seeding fresh references onto a
 *     soft-deleted calibre, or onto the placeholder that absorbs other brands'
 *     references, would be creating exactly the mess those flags exist to stop.
 *
 * Note what is NOT required: a tier. Gating on one would make this find nothing
 * — a tier only means something on a calibre that defines `tiers[]`, and the
 * generic base movements micro-brands fit define none. The tier is copied when
 * the parent has a real one and skipped otherwise.
 */
export function buildParentCalibreFixes(
  refsById: ReadonlyMap<string, WatchReference>,
  calibres: readonly CustomCalibreEntry[],
  brand: { id?: string; name?: string },
): ParentCalibreFix[] {
  const calibresById = buildCalibreIdIndex(calibres);
  const fixes: ParentCalibreFix[] = [];

  for (const ref of refsById.values()) {
    if (displayCalibreForRef(ref, calibresById)) continue;
    if (!deriveIsVariant(ref.parentReferenceId)) continue;

    const parent = refsById.get((ref.parentReferenceId as string).trim());
    if (!parent) continue;
    if (deriveIsVariant(parent.parentReferenceId)) continue;

    const entry = findRefCalibreEntry(calibres, parent, brand, { byId: calibresById });
    if (!entry) continue;
    if (isDeprecatedCalibre(entry) || isCatchAllCalibre(entry)) continue;

    const parentTier = (parent.calibreTierId || '').trim();
    const tierId =
      parentTier && !isTierStaleOnCalibre(parentTier, entry.data?.tiers)
        ? parentTier
        : undefined;

    fixes.push({ ref, parent, entry, tierId });
  }

  return fixes;
}

/**
 * The write payload for one {@link ParentCalibreFix}.
 *
 * The calibre half is {@link buildRefUpdates} unchanged, so this inherits every
 * rule the Apply button already answers to: both dual-write feature flags,
 * fill-empty-only on the movement scalars, exact mirroring of the booleans, and
 * the `calibreId` link with its orphan-tier guard.
 *
 * The tier is then layered on top, and the ORDER matters. `buildRefUpdates` may
 * have queued a `deleteField()` for a tier the variant carries that the new
 * calibre does not define; writing the parent's valid tier afterwards replaces
 * that clear with the right value rather than racing it. When the parent has no
 * usable tier the clear stands, which is correct — a dangling pointer should go.
 */
export function buildParentCalibreUpdates(
  fix: ParentCalibreFix,
  flags: CalibreDualWriteFlags,
): Record<string, unknown> {
  const updates = buildRefUpdates(fix.ref, fix.entry, flags);
  if (fix.tierId && fix.ref.calibreTierId !== fix.tierId) {
    updates.calibreTierId = fix.tierId;
  }
  return updates;
}

/** Commit `{ref, updates}` pairs in doc order, one batch per chunk. Shared by
 *  both Apply paths — Firestore caps a batch at 500 ops and a group can carry
 *  several hundred references, so neither may issue one `updateDoc` per ref. */
export async function commitRefUpdates(
  brandId: string,
  pending: { ref: WatchReference; updates: Record<string, unknown> }[],
): Promise<void> {
  for (let i = 0; i < pending.length; i += REF_WRITE_BATCH_SIZE) {
    const chunk = pending.slice(i, i + REF_WRITE_BATCH_SIZE);
    const batch = writeBatch(db);
    for (const { ref, updates } of chunk) {
      batch.update(
        doc(db, WATCH_BRANDS_COLLECTION, brandId, REFERENCES_SUBCOLLECTION, ref.id),
        updates,
      );
    }
    await batch.commit();
  }
}

/** Push a calibre's data to every ref in the group, filling empty fields only.
 *  Returns the updated refs (in-memory copies with the applied updates) and
 *  counts so the caller can show a toast.
 *
 *  Writes go out in batches rather than one `updateDoc` per ref: a group of 300
 *  refs was 300 sequential round trips, and each landed as its own trigger
 *  fan-out. `runToken` is the caller's per-Apply-run token (see
 *  `shouldStampBulkToken`). */
export async function pushCalibreDataToRefs(
  brandId: string,
  refs: WatchReference[],
  entry: Pick<CustomCalibreEntry, 'id' | 'name' | 'data'>,
  flags: CalibreDualWriteFlags,
  runToken: string,
  options: {
    /**
     * Stamp {@link CALIBRE_RECOUNT_DEFER_TOKEN_FIELD} on every write so
     * watchlock's `maintainCalibreReferenceCount` skips its per-write recount.
     * ONLY for a caller that then asks for one targeted recount itself
     * (`recomputeCalibreReferenceCounts`) — otherwise the calibre's
     * `referenceCount` is left stale. Content written is unchanged.
     */
    deferCalibreRecount?: boolean;
  } = {},
): Promise<{ updatedRefs: WatchReference[]; updatedCount: number; fieldsWritten: number }> {
  const updatedRefs: WatchReference[] = [];
  let updatedCount = 0;
  let fieldsWritten = 0;

  // Pass 1 — build every write payload up front, preserving the caller's ref
  // order in `updatedRefs` (the enricher's cached group order depends on it).
  const pending: { ref: WatchReference; updates: Record<string, unknown> }[] = [];
  for (const ref of refs) {
    const updates = buildRefUpdates(ref, entry, flags);
    if (Object.keys(updates).length === 0) {
      updatedRefs.push(ref);
      continue;
    }
    // Counted before the bookkeeping fields are added, so the toast keeps
    // reporting real content fields.
    fieldsWritten += Object.keys(updates).length;
    updates.updatedAt = Timestamp.now();
    if (shouldStampBulkToken(ref, updates)) {
      updates.__bulkWriteToken = runToken;
    }
    if (options.deferCalibreRecount) {
      updates[CALIBRE_RECOUNT_DEFER_TOKEN_FIELD] = runToken;
    }
    pending.push({ ref, updates });
    updatedRefs.push(applyUpdatesToRef(ref, updates));
    updatedCount++;
  }

  // Pass 2 — commit in order. A failing commit throws and the caller's
  // per-group catch reports the whole group as failed, which is the same
  // granularity the per-ref loop gave it.
  await commitRefUpdates(brandId, pending);

  return { updatedRefs, updatedCount, fieldsWritten };
}

/**
 * Push each variant's PARENT calibre onto it. Returns how many were written.
 *
 * `runToken` and {@link shouldStampBulkToken} carry over from the sibling Apply
 * for the same reason, and the answer here is usually "don't stamp": every
 * target is a variant by construction, and with `legacyCalibreString` on (the
 * default) the payload sets top-level `calibre` — one of the seven fields
 * watchlock aggregates onto the parent. Suppressing that recompute would drift
 * the parent's `movementVariants` permanently. With the flag off there is no
 * aggregated field in the payload and the token is stamped, skipping a fan-out
 * that would have computed nothing.
 */
export async function pushParentCalibresToVariants(
  brandId: string,
  fixes: ParentCalibreFix[],
  flags: CalibreDualWriteFlags,
  runToken: string,
): Promise<{ updatedCount: number; fieldsWritten: number }> {
  const pending: { ref: WatchReference; updates: Record<string, unknown> }[] = [];
  let fieldsWritten = 0;

  for (const fix of fixes) {
    const updates = buildParentCalibreUpdates(fix, flags);
    if (Object.keys(updates).length === 0) continue;
    fieldsWritten += Object.keys(updates).length;
    updates.updatedAt = Timestamp.now();
    if (shouldStampBulkToken(fix.ref, updates)) {
      updates.__bulkWriteToken = runToken;
    }
    pending.push({ ref: fix.ref, updates });
  }

  await commitRefUpdates(brandId, pending);
  return { updatedCount: pending.length, fieldsWritten };
}
